import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserButton, useUser } from '@clerk/clerk-react';

import ImageWithFallback from '../components/ImageWithFallback';
import MultiplayerSessionManager from '../components/MultiplayerSessionManager/MultiplayerSessionManager';
import {
  PREMIUM_PLAN_NAME,
  PREMIUM_PRICE_LABEL,
  UPGRADE_ROUTE,
  buildUsageStatus,
  getSessionExpirationDate,
  getTierConfig,
  isPremiumTier,
  isSessionExpired
} from '../config/entitlements';
import { resolveCharacterArtUrl, resolveMonsterArtUrl } from '../services/ArtworkResolver';
import CustomAssetManager from '../services/CustomAssetManager';
import DataMigration from '../services/DataMigration';
import { getAssetUsageSnapshot, getCustomEntityUsageSnapshot } from '../services/EntitlementService';
import ImportExportService from '../services/ImportExportService';
import SessionManager from '../services/SessionManager';
import UnifiedDataManager from '../services/UnifiedDataManager';
import {
  getNewVaultEditorRoute,
  inferCharacterCategory,
  writeCharacterCategory
} from '../utils/dashboardVaultState';
import { resolveTierFromUser } from '../utils/runtimeTier';

import './Dashboard.css';

const GM_MODE_FEATURES = [
  {
    name: 'Encounter Presets',
    detail: 'Bank ambushes, boss fights, and reusable encounter loadouts for later sessions.',
    status: 'Premium'
  },
  {
    name: 'House Rule Forge',
    detail: 'Tune crits, rests, and table rulings so your campaign plays by your rules.',
    status: 'Coming Soon',
    href: '/ruleset'
  },
  {
    name: 'Campaign Continuity',
    detail: "Keep saved battle rooms available without the Free plan's 30-day expiration.",
    status: 'Premium'
  }
];

const matchesQuery = (values, query) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return true;
  }

  return values.some((value) => String(value ?? '').toLowerCase().includes(normalizedQuery));
};

const formatSavedAt = (timestamp) => {
  if (!timestamp) {
    return 'Unknown save time';
  }

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return 'Unknown save time';
  }

  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} at ${date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit'
  })}`;
};

const formatExpiresAt = (timestamp) => {
  if (!timestamp) {
    return null;
  }

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} at ${date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit'
  })}`;
};

const getSessionDetails = (session) => ({
  tokenCount: session?.metadata?.tokenCount ?? session?.data?.battleState?.tokens?.length ?? 0,
  hasMap: session?.metadata?.hasMap ?? Boolean(session?.data?.battleState?.map),
  gridSize: session?.metadata?.gridSize ?? session?.data?.battleState?.gridSize ?? 50
});

const getCurrentHp = (hp) => (typeof hp === 'object' && hp !== null ? hp.current ?? hp.value ?? null : hp ?? null);
const getMaxHp = (hp) => (typeof hp === 'object' && hp !== null ? hp.maximum ?? hp.max ?? null : hp ?? null);
const getDisplayAc = (displayAc, data = {}) => {
  const candidates = [
    displayAc,
    data.ac?.value,
    data.ac?.base,
    data.ac,
    data.armorClass?.value,
    data.armorClass?.base,
    data.armorClass,
    data.defense?.ac?.base,
    data.defense?.ac?.value,
    data.combat?.ac,
    data.stats?.ac
  ];

  for (const candidate of candidates) {
    if (candidate == null) {
      continue;
    }

    if (typeof candidate === 'object') {
      const nestedValue = candidate.value ?? candidate.base ?? null;
      if (nestedValue != null) {
        return nestedValue;
      }
      continue;
    }

    if (String(candidate).trim()) {
      return candidate;
    }
  }

  return null;
};

const downloadJson = (payload, filename) => {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const pickJsonFile = (onFile) => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.json';
  input.onchange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      onFile(file);
    }
  };
  input.click();
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const tier = resolveTierFromUser(user);
  const tierConfig = getTierConfig(tier);
  const isPremium = isPremiumTier(tier);

  const [savedSessions, setSavedSessions] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [monsters, setMonsters] = useState([]);
  const [characterUsage, setCharacterUsage] = useState({ count: 0, items: [] });
  const [monsterUsage, setMonsterUsage] = useState({ count: 0, items: [] });
  const [assetUsage, setAssetUsage] = useState({ totalCount: 0, totalBytes: 0 });
  const [activeVaultTab, setActiveVaultTab] = useState('heroes');
  const [sessionSearch, setSessionSearch] = useState('');
  const [characterSearch, setCharacterSearch] = useState('');
  const [npcSearch, setNpcSearch] = useState('');
  const [monsterSearch, setMonsterSearch] = useState('');

  const refreshSessions = () => {
    const sessions = SessionManager.getAllSessions();
    setSavedSessions(Array.isArray(sessions) ? sessions : []);
  };

  const loadCharacters = async () => {
    try {
      let entities = Object.values((await UnifiedDataManager.getAllEntities('character')) || {});
      if (entities.length < 7) {
        await DataMigration.migratePreloadedCharacters();
        entities = Object.values((await UnifiedDataManager.getAllEntities('character')) || {});
      }

      setCharacters(entities.map((entity, index) => {
        const data = entity.data || {};
        const display = UnifiedDataManager.getDisplayData(entity);
        const tokenKey = data.tokenAssetKey || data.tokenAsset?.key || entity._meta?.tokenAssetKey || entity._meta?.tokenAsset?.key;
        const normalizedTokenKey = tokenKey ? String(tokenKey).replace(/^\/+/, '') : null;
        return {
          id: entity.id,
          uniqueKey: `character_${entity.id}_${index}`,
          originalId: entity.id,
          name: display.name || data.identity?.name || data.name || 'Unknown Hero',
          class: display.class || data.classes?.[0]?.name || data.class || 'Unknown Calling',
          level: display.level,
          race: data.identity?.race || data.race || 'Unknown Lineage',
          hp: display.hp,
          maxHp: display.maxHp,
          ac: getDisplayAc(display.ac, data),
          isCustom: !!entity.tags?.includes('custom'),
          libraryCategory: inferCharacterCategory(entity, display.name || data.identity?.name || data.name),
          source: 'unified',
          artworkSrc: resolveCharacterArtUrl({
            ...data,
            id: data.id || entity.id,
            tokenAssetKey: normalizedTokenKey || data.tokenAssetKey,
            tokenAsset: normalizedTokenKey ? { key: normalizedTokenKey } : data.tokenAsset,
            artwork: entity._meta?.artwork || data.artwork || data.imageUrl || data.imageKey || data.artKey
          })
        };
      }));
    } catch (error) {
      console.error('[Dashboard] Failed to load characters:', error);
      setCharacters([]);
    }
  };

  const loadMonsters = async () => {
    try {
      let entities = Object.values((await UnifiedDataManager.getAllEntities('monster')) || {});
      if (entities.length < 10) {
        await DataMigration.migratePreloadedMonsters();
        entities = Object.values((await UnifiedDataManager.getAllEntities('monster')) || {});
      }

      setMonsters(entities.map((entity, index) => {
        const data = entity.data || {};
        const display = UnifiedDataManager.getDisplayData({ type: 'monster', id: entity.id, data });
        const tokenKey = data.tokenAssetKey || data.tokenAsset?.key || entity._meta?.tokenAssetKey || entity._meta?.tokenAsset?.key;
        const normalizedTokenKey = tokenKey ? String(tokenKey).replace(/^\/+/, '') : null;
        return {
          id: entity.id,
          uniqueKey: `monster_${entity.id}_${index}`,
          originalId: entity.id,
          name: data.name || 'Unknown Monster',
          // Canonical challenge.cr is shared with the roster and sheet.
          // date_of_change: 2026-11-05
          cr: display.cr,
          hp: display.hp,
          maxHp: display.maxHp,
          ac: display.ac,
          isCustom: !!entity.tags?.includes('custom'),
          artworkSrc: resolveMonsterArtUrl({
            ...data,
            id: data.id || entity.id,
            tokenAssetKey: normalizedTokenKey || data.tokenAssetKey,
            tokenAsset: normalizedTokenKey ? { key: normalizedTokenKey } : data.tokenAsset,
            artwork: entity._meta?.artwork || data.artwork || data.imageUrl || data.imageKey || data.artKey
          })
        };
      }));
    } catch (error) {
      console.error('[Dashboard] Failed to load monsters:', error);
      setMonsters([]);
    }
  };

  const refreshEntitlementUsage = async () => {
    try {
      const [nextCharacterUsage, nextMonsterUsage, nextAssetUsage] = await Promise.all([
        getCustomEntityUsageSnapshot('character'),
        getCustomEntityUsageSnapshot('monster'),
        getAssetUsageSnapshot()
      ]);

      setCharacterUsage(nextCharacterUsage);
      setMonsterUsage(nextMonsterUsage);
      setAssetUsage(nextAssetUsage);
    } catch (error) {
      console.error('[Dashboard] Failed to refresh entitlement usage:', error);
    }
  };

  useEffect(() => {
    refreshSessions();
    loadCharacters();
    loadMonsters();
    refreshEntitlementUsage();

    const handleFocus = () => {
      refreshSessions();
      loadCharacters();
      loadMonsters();
      refreshEntitlementUsage();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const handleLoadSavedSession = async (session) => {
    try {
      if (isSessionExpired(session)) {
        alert(`"${session.name}" has expired. Delete it to reclaim the Free slot, or upgrade to ${PREMIUM_PLAN_NAME} to retain future saves.`);
        return;
      }

      const loadedSession = await SessionManager.loadSession(session.id);
      const payload = loadedSession?.battleState ? loadedSession : session.data && Object.keys(session.data).length > 0 ? session.data : session;
      if (!payload) {
        throw new Error('Session data was empty');
      }

      navigate('/battleroom', {
        state: {
          loadSessionData: payload,
          sessionName: session.name,
          fromDashboard: true,
          forceGM: true
        }
      });
    } catch (error) {
      console.error('[Dashboard] Failed to load session:', error);
      if (error?.code === 'SESSION_EXPIRED') {
        alert(`"${session.name}" has expired. Delete it to reclaim the Free slot, or upgrade to ${PREMIUM_PLAN_NAME} to retain future saves.`);
        return;
      }

      alert(`Unable to load "${session.name}". Use "Load session file" from Keeper's Tools instead.`);
    }
  };

  const handleDeleteSession = (sessionId, sessionName) => {
    if (!window.confirm(`Delete "${sessionName}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    if (SessionManager.deleteSession(sessionId)) {
      refreshSessions();
      alert(`"${sessionName}" has been deleted.`);
      return;
    }

    alert('Failed to delete session. Please try again.');
  };

  const handleLoadSessionFile = () => {
    pickJsonFile((file) => {
      navigate('/battleroom', {
        state: {
          loadSessionFile: file,
          fromDashboard: true,
          forceGM: true
        }
      });
    });
  };

  const handleDownloadCharacter = async (character) => {
    try {
      const exportDoc = await ImportExportService.exportCharacter(character.originalId || character.id);
      const filename = `${String(exportDoc.data?.identity?.name || exportDoc.data?.name || character.name || 'character').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_character.json`;
      downloadJson(exportDoc, filename);
    } catch (error) {
      console.error('[Dashboard] Failed to export character:', error);
      alert('Failed to download character. Please try again.');
    }
  };

  const handleDownloadMonster = async (monster) => {
    try {
      const exportDoc = await ImportExportService.exportMonster(monster.originalId || monster.id);
      const filename = `${String(exportDoc.data?.name || monster.name || 'monster').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_monster.json`;
      downloadJson(exportDoc, filename);
    } catch (error) {
      console.error('[Dashboard] Failed to export monster:', error);
      alert('Failed to download monster. Please try again.');
    }
  };

  const handleDeleteCharacter = async (character) => {
    if (!window.confirm(`Delete "${character.name}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      const targetId = character.originalId || character.id;
      const success = character.source === 'unified'
        ? await UnifiedDataManager.deleteEntity(targetId, 'characters')
        : await CustomAssetManager.deleteCustomAsset(targetId);

      if (!success) {
        throw new Error('Delete returned false');
      }

      await loadCharacters();
      await refreshEntitlementUsage();
    } catch (error) {
      console.error('[Dashboard] Failed to delete character:', error);
      alert('Failed to delete character. Please try again.');
    }
  };

  const handleDeleteMonster = async (monster) => {
    if (!window.confirm(`Delete "${monster.name}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      const success = await CustomAssetManager.deleteCustomAsset(monster.originalId || monster.id);
      if (!success) {
        throw new Error('Delete returned false');
      }

      await loadMonsters();
      await refreshEntitlementUsage();
    } catch (error) {
      console.error('[Dashboard] Failed to delete monster:', error);
      alert('Failed to delete monster. Please try again.');
    }
  };

  const handleUploadCharacter = (category = 'hero') => {
    pickJsonFile((file) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const payload = JSON.parse(event.target.result);
          const imported = await ImportExportService.importCharacter(payload);
          if (!imported?.id) {
            throw new Error('Import did not return an id');
          }

          writeCharacterCategory(imported.id, category);
          await loadCharacters();
          await refreshEntitlementUsage();
          alert(`Imported ${imported.data?.identity?.name || imported.data?.name || file.name} to ${category === 'npc' ? 'NPCs' : 'Heroes'}.`);
        } catch (error) {
          console.error('[Dashboard] Failed to import character:', error);
          alert('Failed to import character. Please check the file and try again.');
        }
      };
      reader.readAsText(file);
    });
  };

  const handleUploadMonster = () => {
    pickJsonFile((file) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const monsterData = JSON.parse(event.target.result);
          const artworkHint = monsterData?.artwork || monsterData?.imageUrl || monsterData?.data?.artwork || monsterData?.data?.imageUrl;
          const payload = monsterData?.type === 'monster' && monsterData?.data
            ? monsterData
            : {
                ...(typeof monsterData === 'object' ? monsterData : {}),
                type: monsterData?.type || 'monster',
                ...(artworkHint ? { artwork: artworkHint } : {})
              };

          const imported = await ImportExportService.importMonster(payload);
          if (!imported?.id) {
            throw new Error('Import did not return an id');
          }

          await loadMonsters();
          await refreshEntitlementUsage();
          alert(`Imported ${imported.data?.name || imported.name || file.name}.`);
        } catch (error) {
          console.error('[Dashboard] Failed to import monster:', error);
          alert('Failed to import monster. Please check the file and try again.');
        }
      };
      reader.readAsText(file);
    });
  };

  const displayName = user?.firstName || user?.fullName || 'Dungeon Master';
  const sessionStatus = buildUsageStatus(tierConfig.limits.savedSessions, savedSessions.length);
  const characterStatus = buildUsageStatus(tierConfig.limits.customCharacters, characterUsage.count);
  const monsterStatus = buildUsageStatus(tierConfig.limits.customMonsters, monsterUsage.count);
  const assetStatus = buildUsageStatus(tierConfig.limits.assetCount, assetUsage.totalCount);
  const expiredSessionCount = savedSessions.filter((session) => isSessionExpired(session)).length;
  const recentSessions = [...savedSessions].sort((a, b) => new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime());
  const filteredSessions = recentSessions.filter((session) => {
    const details = getSessionDetails(session);
    return matchesQuery([session?.name, session?.id, details.tokenCount, details.gridSize, details.hasMap ? 'map' : 'no map'], sessionSearch);
  });
  const heroCharacters = characters.filter((character) => character.libraryCategory !== 'npc');
  const npcCharacters = characters.filter((character) => character.libraryCategory === 'npc');
  const filteredHeroes = [...heroCharacters]
    .sort((a, b) => Number(b.isCustom) - Number(a.isCustom) || String(a.name || '').localeCompare(String(b.name || '')))
    .filter((character) => matchesQuery([character.name, character.class, character.level, character.race, character.isCustom ? 'custom' : 'template'], characterSearch));
  const filteredNpcs = [...npcCharacters]
    .sort((a, b) => Number(b.isCustom) - Number(a.isCustom) || String(a.name || '').localeCompare(String(b.name || '')))
    .filter((character) => matchesQuery([character.name, character.class, character.level, character.race, character.isCustom ? 'custom' : 'template', 'npc'], npcSearch));
  const filteredMonsters = [...monsters]
    .sort((a, b) => Number(b.isCustom) - Number(a.isCustom) || String(a.name || '').localeCompare(String(b.name || '')))
    .filter((monster) => matchesQuery([monster.name, monster.cr, monster.isCustom ? 'custom' : 'template'], monsterSearch));
  const activeVaultItems = activeVaultTab === 'heroes' ? filteredHeroes : activeVaultTab === 'npcs' ? filteredNpcs : filteredMonsters;
  const activeSearch = activeVaultTab === 'heroes' ? characterSearch : activeVaultTab === 'npcs' ? npcSearch : monsterSearch;
  const activePlaceholder = activeVaultTab === 'heroes'
    ? 'Search by hero, class, lineage, or level'
    : activeVaultTab === 'npcs'
      ? 'Search by NPC, role, lineage, or level'
      : 'Search by monster, CR, or custom tag';
  const activeSearchLabel = activeVaultTab === 'heroes' ? 'Find a hero' : activeVaultTab === 'npcs' ? 'Find an NPC' : 'Find a monster';
  const activeImportLabel = 'Import';
  const activeImportLocked = activeVaultTab === 'monsters' ? monsterStatus.atLimit : characterStatus.atLimit;
  const activeVaultCopy = activeVaultTab === 'monsters'
    ? (monsterStatus.isUnlimited
      ? `${PREMIUM_PLAN_NAME}: create custom monsters without the Free plan entry limit.`
      : `Free monster vault: ${monsterStatus.used}/${monsterStatus.limit} custom monsters. Retire one to import another.`)
    : (characterStatus.isUnlimited
      ? `${PREMIUM_PLAN_NAME}: create custom heroes and NPCs without the Free plan entry limit.`
      : `Free hero roster: ${characterStatus.used}/${characterStatus.limit} custom heroes or NPCs. Retire one to import another.`);
  const handleActiveImport = () => {
    if (activeImportLocked) {
      alert(activeVaultTab === 'monsters'
        ? `Your free monster vault is full. Retire one or upgrade to ${PREMIUM_PLAN_NAME}.`
        : `Your free hero roster is full. Retire one or upgrade to ${PREMIUM_PLAN_NAME}.`);
      return;
    }

    if (activeVaultTab === 'monsters') {
      handleUploadMonster();
      return;
    }

    handleUploadCharacter(activeVaultTab === 'npcs' ? 'npc' : 'hero');
  };

  const handleActiveNew = () => {
    if (activeImportLocked) {
      alert(activeVaultTab === 'monsters'
        ? `Your free monster vault is full. Retire one or upgrade to ${PREMIUM_PLAN_NAME}.`
        : `Your free hero roster is full. Retire one or upgrade to ${PREMIUM_PLAN_NAME}.`);
      return;
    }

    navigate(getNewVaultEditorRoute(activeVaultTab));
  };

  return (
    <div className="dashboard">
      <div className="dashboard-shell">
        <section className="dashboard-hero dashboard-hero--compact">
          <div className="dashboard-hero-copy">
            <div className="dashboard-title-row">
              <h1 className="dashboard-title">Virtual Combat Simulator</h1>
              <div className="dashboard-user-inline">
                <div className="dashboard-user-inline__meta">
                  <strong>{displayName}</strong>
                </div>
                <UserButton appearance={{ elements: { avatarBox: 'dashboard-user-avatar' } }} />
              </div>
            </div>
            <p className="dashboard-lead dashboard-tier-copy">
              {isPremium
                ? `${PREMIUM_PLAN_NAME} is active. Continue saved battles without the Free plan's 30-day expiration, manage custom roster entries, and store up to 1 GB of uploaded maps and tokens.`
                : 'Free includes one saved battle retained for 30 days, 2 custom heroes or NPCs, 2 custom monsters, and 10 uploaded maps or tokens.'}
            </p>
            <div className="dashboard-stat-strip">
              <div className="dashboard-stat-pill">
                <span>Tier</span>
                <strong>{isPremium ? PREMIUM_PLAN_NAME : 'Free'}</strong>
              </div>
              <div className="dashboard-stat-pill">
                <span>Battle Saves</span>
                <strong>{sessionStatus.isUnlimited ? 'Unlimited' : `${sessionStatus.used}/${sessionStatus.limit}`}</strong>
              </div>
              <div className="dashboard-stat-pill">
                <span>Hero Roster</span>
                <strong>{characterStatus.isUnlimited ? 'Unlimited' : `${characterStatus.used}/${characterStatus.limit}`}</strong>
              </div>
              <div className="dashboard-stat-pill">
                <span>Monster Vault</span>
                <strong>{monsterStatus.isUnlimited ? 'Unlimited' : `${monsterStatus.used}/${monsterStatus.limit}`}</strong>
              </div>
              <div className="dashboard-stat-pill">
                <span>Asset Vault</span>
                <strong>{assetStatus.isUnlimited ? 'Unlimited' : `${assetStatus.used}/${assetStatus.limit}`}</strong>
              </div>
            </div>
            <div className="dashboard-hero-actions">
              {!isPremium && (
                <Link to={UPGRADE_ROUTE} className="dashboard-button dashboard-button--primary">
                  Unlock {PREMIUM_PLAN_NAME}
                </Link>
              )}
            </div>
          </div>
        </section>

        <section className="dashboard-main-grid">
          <div className="dashboard-panel dashboard-panel--command dashboard-panel--future">
            <div className="dashboard-panel-header">
              <div>
                <h2>Game Master</h2>
              </div>
              <span className="dashboard-panel-badge">
                {isPremium ? 'Active' : PREMIUM_PRICE_LABEL}
              </span>
            </div>
            <p className="dashboard-panel-note">
              {isPremium
                ? 'Your Game Master tools are active. Reuse encounter presets, maintain campaign saves, and organize custom heroes, NPCs, and monsters.'
                : `Free includes the core combat tools. ${PREMIUM_PLAN_NAME} adds encounter presets, saved-room continuity, expanded custom roster capacity, and a larger asset vault.`}
            </p>
            <div className="dashboard-future-showcase">
              {!isPremium && <span className="dashboard-coming-soon-seal">Premium Perks</span>}
              <div className="dashboard-rules-grid dashboard-rules-grid--compact">
                <div className="dashboard-rules-card">
                  <span className="dashboard-rules-card__status">Included</span>
                  <strong>Core Rules Ready</strong>
                  <p>Jump straight into initiative, HP, conditions, Fog of War, and shared table control.</p>
                </div>
                {GM_MODE_FEATURES.map((entry) => {
                  const isLocked = !isPremium;
                  const isComingSoon = entry.status === 'Coming Soon';
                  const isDisabled = isLocked || isComingSoon;
                  return (
                    <div key={entry.name} className={`dashboard-rules-card${isDisabled ? ' dashboard-rules-card--disabled' : ''}`}>
                      <span className="dashboard-rules-card__status">{entry.status}</span>
                      <strong>{entry.name}</strong>
                      <p>{entry.detail}</p>
                      {entry.href && !isDisabled ? (
                        <Link to={entry.href} className="dashboard-mini-button dashboard-mini-button--primary">
                          Open
                        </Link>
                      ) : isComingSoon ? (
                        <span className="dashboard-chip">Coming Soon</span>
                      ) : !isLocked ? (
                        <span className="dashboard-chip">Campaign Ready</span>
                      ) : (
                        <Link
                          to={UPGRADE_ROUTE}
                          className="dashboard-mini-button dashboard-mini-button--ghost"
                        >
                          Upgrade for {entry.name}
                        </Link>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="dashboard-panel dashboard-panel--sessions" id="battle-rooms-panel">
            <div className="dashboard-panel-header">
              <div className="dashboard-panel-heading">
                <h2>Battle Rooms</h2>
              </div>
            </div>
            <div className="dashboard-battle-room-shell">
              <MultiplayerSessionManager onSessionJoined={(id) => navigate(`/battle/${id}`)} onSessionCreated={(session) => navigate(`/battle/${session.id}`)} />
            </div>
            <div className="dashboard-panel-section">
              <div className="dashboard-inline-header">
                <h3>Saved Battle Rooms</h3>
                <span>{filteredSessions.length} shown</span>
              </div>
              <div className="dashboard-tier-note">
                {isPremium
                  ? "Saved battle rooms remain available without the Free plan's 30-day expiration."
                  : `Free slot ${Math.min(sessionStatus.used, sessionStatus.limit)}/${sessionStatus.limit}. Saved battle rooms expire after 30 days.${expiredSessionCount > 0 ? ` ${expiredSessionCount} save${expiredSessionCount !== 1 ? 's have' : ' has'} expired.` : ''}`}
                {!isPremium && (
                  <Link to={UPGRADE_ROUTE} className="dashboard-text-link">
                    Upgrade
                  </Link>
                )}
              </div>
              <div className="dashboard-toolbar">
                <label className="dashboard-search">
                  <span>Find a saved battle room</span>
                  <input type="search" value={sessionSearch} onChange={(event) => setSessionSearch(event.target.value)} placeholder="Search by battle name, Teleportation Circle, or map" />
                </label>
                <button type="button" className="dashboard-mini-button" onClick={handleLoadSessionFile}>
                  Load File
                </button>
              </div>
              <div className="dashboard-list-shell dashboard-list-shell--sessions">
                {filteredSessions.length === 0 ? (
                  <div className="dashboard-empty-state">
                    <strong>No saved battle rooms match this search.</strong>
                    <p>Create a new battle room or load a saved room file.</p>
                    <div className="dashboard-empty-actions">
                      <button type="button" className="dashboard-mini-button" onClick={handleLoadSessionFile}>Load Room File</button>
                    </div>
                  </div>
                ) : (
                  filteredSessions.map((session) => {
                    const details = getSessionDetails(session);
                    const expired = isSessionExpired(session);
                    const expiresAt = formatExpiresAt(getSessionExpirationDate(session));
                    return (
                      <article key={session.id} className={`dashboard-session-card${expired ? ' is-expired' : ''}`}>
                        <div className="dashboard-session-card__copy">
                          <div className="dashboard-card-title-row">
                            <h3>{session.name || 'Unnamed battle room'}</h3>
                            <span className="dashboard-chip">{expired ? 'Expired' : details.hasMap ? 'Map ready' : 'No map'}</span>
                          </div>
                          <p>{details.tokenCount} combatant{details.tokenCount !== 1 ? 's' : ''} • {details.gridSize}px grid</p>
                          <p className="dashboard-muted-text">
                            {expired
                              ? 'This save has expired. Delete it to reclaim the slot.'
                              : `Saved ${formatSavedAt(session.createdAt)}${expiresAt ? ` • Expires ${expiresAt}` : ''}`}
                          </p>
                        </div>
                        <div className="dashboard-card-actions">
                          <button
                            type="button"
                            className="dashboard-mini-button dashboard-mini-button--primary"
                            onClick={() => handleLoadSavedSession(session)}
                            disabled={expired}
                            title={expired ? `Upgrade to ${PREMIUM_PLAN_NAME} or clear this slot.` : 'Load battle'}
                          >
                            {expired ? 'Unavailable' : 'Load'}
                          </button>
                          <button type="button" className="dashboard-mini-button" onClick={() => SessionManager.downloadSession(session)}>Download</button>
                          {!isPremium && expired && (
                            <Link to={UPGRADE_ROUTE} className="dashboard-mini-button dashboard-mini-button--ghost">
                              Upgrade
                            </Link>
                          )}
                          <button type="button" className="dashboard-mini-button dashboard-mini-button--danger" onClick={() => handleDeleteSession(session.id, session.name)}>Delete</button>
                        </div>
                      </article>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <div className="dashboard-panel dashboard-panel--compendium">
            <div className="dashboard-panel-header">
              <h2>Heroes, NPCs and Monsters</h2>
              <div className="dashboard-tab-strip" role="tablist" aria-label="Compendium sections">
                <button type="button" role="tab" aria-selected={activeVaultTab === 'heroes'} className={`dashboard-tab-button${activeVaultTab === 'heroes' ? ' is-active' : ''}`} onClick={() => setActiveVaultTab('heroes')}>Heroes</button>
                <button type="button" role="tab" aria-selected={activeVaultTab === 'npcs'} className={`dashboard-tab-button${activeVaultTab === 'npcs' ? ' is-active' : ''}`} onClick={() => setActiveVaultTab('npcs')}>NPCs</button>
                <button type="button" role="tab" aria-selected={activeVaultTab === 'monsters'} className={`dashboard-tab-button${activeVaultTab === 'monsters' ? ' is-active' : ''}`} onClick={() => setActiveVaultTab('monsters')}>Monsters</button>
              </div>
            </div>
            <div className="dashboard-tier-note">
              {activeVaultCopy}
              {!isPremium && activeImportLocked && (
                <Link to={UPGRADE_ROUTE} className="dashboard-text-link">
                  Unlock {PREMIUM_PLAN_NAME}
                </Link>
              )}
            </div>
            <div className="dashboard-toolbar dashboard-toolbar--roster">
              <label className="dashboard-search dashboard-search--compact">
                <span>{activeSearchLabel}</span>
                <input
                  type="search"
                  value={activeSearch}
                  onChange={(event) => {
                    if (activeVaultTab === 'heroes') {
                      setCharacterSearch(event.target.value);
                    } else if (activeVaultTab === 'npcs') {
                      setNpcSearch(event.target.value);
                    } else {
                      setMonsterSearch(event.target.value);
                    }
                  }}
                  placeholder={activePlaceholder}
                />
              </label>
              <button
                type="button"
                className={`dashboard-mini-button${activeImportLocked ? ' is-disabled' : ''}`}
                onClick={handleActiveNew}
                disabled={activeImportLocked}
                title={activeImportLocked ? `Upgrade to ${PREMIUM_PLAN_NAME} or retire one custom entry.` : `Create a new ${activeVaultTab === 'monsters' ? 'monster' : activeVaultTab === 'npcs' ? 'NPC' : 'hero'}`}
              >
                New
              </button>
              <button
                type="button"
                className={`dashboard-mini-button dashboard-mini-button--primary${activeImportLocked ? ' is-disabled' : ''}`}
                onClick={handleActiveImport}
                disabled={activeImportLocked}
                title={activeImportLocked ? `Upgrade to ${PREMIUM_PLAN_NAME} or retire one custom entry.` : activeImportLabel}
              >
                {activeImportLabel}
              </button>
            </div>
            <div className="dashboard-list-shell dashboard-list-shell--compendium">
              {activeVaultItems.length === 0 ? (
                <div className="dashboard-empty-state">
                  <strong>No entries match this search.</strong>
                  <p>Try a broader term or import a fresh entry into the vault.</p>
                </div>
              ) : activeVaultTab === 'heroes' || activeVaultTab === 'npcs' ? (
                activeVaultItems.map((character) => (
                  <article key={character.uniqueKey || character.id} className={`dashboard-library-card${character.isCustom ? ' is-custom' : ''}`}>
                    <div className="dashboard-library-card__media">
                      <ImageWithFallback src={character.artworkSrc || resolveCharacterArtUrl(character)} alt={character.name} className="dashboard-portrait" />
                    </div>
                    <div className="dashboard-library-card__copy">
                      <div className="dashboard-card-title-row">
                        <h3>{character.name}</h3>
                        <div className="dashboard-card-flags">
                          {activeVaultTab === 'npcs' && <span className="dashboard-chip">NPC</span>}
                          {character.isCustom && <span className="dashboard-chip">Custom</span>}
                        </div>
                      </div>
                      <p>{character.class} • Level {character.level ?? '—'} • {character.race}</p>
                      <p className="dashboard-muted-text">HP {getCurrentHp(character.hp) ?? '—'}/{getMaxHp(character.maxHp) ?? '—'} • AC {character.ac ?? '—'}</p>
                    </div>
                    <div className="dashboard-card-actions">
                      <Link to={`/character/${character.originalId || character.id}?mode=view`} className="dashboard-mini-button dashboard-mini-button--primary">View</Link>
                      <Link to={`/character/edit/${character.originalId || character.id}`} className="dashboard-mini-button">Edit</Link>
                      <button type="button" className="dashboard-mini-button" onClick={() => handleDownloadCharacter(character)}>Download</button>
                      {character.isCustom && <button type="button" className="dashboard-mini-button dashboard-mini-button--danger" onClick={() => handleDeleteCharacter(character)}>Delete</button>}
                    </div>
                  </article>
                ))
              ) : (
                filteredMonsters.map((monster) => (
                  <article key={monster.uniqueKey || monster.id} className={`dashboard-library-card${monster.isCustom ? ' is-custom' : ''}`}>
                    <div className="dashboard-library-card__media">
                      <ImageWithFallback src={monster.artworkSrc || resolveMonsterArtUrl(monster)} alt={monster.name} className="dashboard-portrait" />
                    </div>
                    <div className="dashboard-library-card__copy">
                      <div className="dashboard-card-title-row">
                        <h3>{monster.name}</h3>
                        {monster.isCustom && <span className="dashboard-chip">Custom</span>}
                      </div>
                      <p>CR {typeof monster.cr === 'object' ? (monster.cr.display ?? monster.cr.value ?? '—') : (monster.cr ?? '—')}</p>
                      <p className="dashboard-muted-text">HP {getCurrentHp(monster.hp) ?? '—'}/{getMaxHp(monster.maxHp) ?? '—'} • AC {monster.ac ?? '—'}</p>
                    </div>
                    <div className="dashboard-card-actions">
                      <Link to={`/monster/${monster.originalId || monster.id}`} className="dashboard-mini-button dashboard-mini-button--primary">View</Link>
                      <Link to={`/monster/edit/${monster.originalId || monster.id}`} className="dashboard-mini-button">Edit</Link>
                      {/* date_of_change: 2026-04-27 - Removed Sheet button; View mode now shows the full stat block */}
                      <button type="button" className="dashboard-mini-button" onClick={() => handleDownloadMonster(monster)}>Download</button>
                      {monster.isCustom && <button type="button" className="dashboard-mini-button dashboard-mini-button--danger" onClick={() => handleDeleteMonster(monster)}>Delete</button>}
                    </div>
                  </article>
                ))
              )}
            </div>
          </div>
        </section>
      </div>

    </div>
  );
};

export default Dashboard;
